import React, { useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import SEO from "../components/SEO";
import TourCard from "../components/TourCard";
import { tours, categories, durations, difficulties } from "../data/tours";

type FilterKey = "category" | "duration" | "difficulty";

interface Filters {
  category: string;
  duration: string;
  difficulty: string;
}

function matchesDuration(durationDays: number, value: string): boolean {
  // Exact lengths now: every tour in the catalogue is 5, 6 or 7 days, so the
  // old short / medium / long buckets put everything in one and left the other
  // two returning nothing.
  if (value === "all") return true;
  return String(durationDays) === value;
}

export default function ToursPage() {
  const [params, setParams] = useSearchParams();

  // The category is DERIVED from the URL, never copied into state. It used to
  // seed useState from the query string, which runs once — so the footer's
  // "Honeymoon Packages" worked when you arrived from another page and did
  // nothing at all when you were already on /tours, because the component
  // stayed mounted and the initialiser never ran again.
  const fromUrl = params.get("category") || "all";
  const category = categories.some((c) => c.value === fromUrl) ? fromUrl : "all";

  const [local, setLocal] = useState({ duration: "all", difficulty: "all" });
  const filters: Filters = { category, ...local };
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  const setFilter = (key: FilterKey, value: string) => {
    if (key === "category") {
      const next = new URLSearchParams(params);
      if (value === "all") next.delete("category");
      else next.set("category", value);
      setParams(next, { replace: true });
      return;
    }
    setLocal((prev) => ({ ...prev, [key]: value }));
  };

  const resetFilters = () => {
    setLocal({ duration: "all", difficulty: "all" });
    const next = new URLSearchParams(params);
    next.delete("category");
    setParams(next, { replace: true });
  };

  const filtered = useMemo(() => {
    return tours.filter((tour) => {
      // A tour carries one or more categories — a region, and any type it is
      // also sold as. Older records with a single string still match.
      const cats: string[] = Array.isArray(tour.category)
        ? tour.category
        : [tour.category];
      const catMatch =
        filters.category === "all" || cats.includes(filters.category);
      const durMatch = matchesDuration(tour.durationDays, filters.duration);
      const diffMatch =
        filters.difficulty === "all" || tour.difficulty === filters.difficulty;
      return catMatch && durMatch && diffMatch;
    });
  }, [filters.category, filters.duration, filters.difficulty]);

  const activeFilterCount = [
    filters.category !== "all",
    filters.duration !== "all",
    filters.difficulty !== "all",
  ].filter(Boolean).length;

  const difficultyBadge: Record<string, string> = {
    easy: "bg-green-100 text-green-700",
    moderate: "bg-yellow-100 text-yellow-700",
    hard: "bg-red-100 text-red-700",
  };

  return (
    <>
      <SEO
        title="Our Expeditions"
        description="Private 5 to 7-day expeditions through Skardu, Baltistan and Hunza, run by a local Gilgit-Baltistan team."
      />

      {/* Hero Banner */}
      <div className="relative bg-primary-900 pt-36 pb-24 px-4 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <polygon fill="white" points="0,100 100,0 100,100" />
          </svg>
        </div>
        <div className="relative max-w-7xl mx-auto text-center">
          <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.18em] text-accent-400 mb-5">
            Skardu · Baltistan · Hunza
          </span>
          <h1 className="font-display text-4xl md:text-6xl font-normal tracking-tight mb-5">
            Our Expeditions
          </h1>
          <p className="text-white/70 text-lg leading-relaxed max-w-2xl mx-auto">
            Five to seven days through the valleys of Gilgit-Baltistan — cold
            deserts, alpine lakes, 400-year-old forts and the Deosai plains.
            Every trip runs privately, in our own 4x4s.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar Filters — desktop */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <FilterPanel
              filters={filters}
              setFilter={setFilter}
              resetFilters={resetFilters}
              activeFilterCount={activeFilterCount}
              difficultyBadge={difficultyBadge}
            />
          </aside>

          <div className="flex-1 min-w-0">
            {/* Mobile filter toggle & result count */}
            <div className="flex items-center justify-between mb-6">
              <p className="text-gray-600 text-sm">
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {filtered.length}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-gray-900">
                  {tours.length}
                </span>{" "}
                tours
              </p>
              <button
                onClick={() => setMobileFiltersOpen(true)}
                className="lg:hidden flex items-center gap-2 bg-primary-600 text-white text-sm font-medium px-4 py-2 rounded-lg"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 4a1 1 0 011-1h16a1 1 0 010 2H4a1 1 0 01-1-1zm3 6a1 1 0 011-1h10a1 1 0 010 2H7a1 1 0 01-1-1zm4 6a1 1 0 011-1h2a1 1 0 010 2h-2a1 1 0 01-1-1z"
                  />
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="bg-white text-primary-600 text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFilterCount}
                  </span>
                )}
              </button>
            </div>

            {/* Active filter chips */}
            {activeFilterCount > 0 && (
              <div className="flex flex-wrap gap-2 mb-6">
                {filters.category !== "all" && (
                  <FilterChip
                    label={`Category: ${
                      categories.find((c) => c.value === filters.category)?.label ||
                      filters.category
                    }`}
                    onRemove={() => setFilter("category", "all")}
                  />
                )}
                {filters.duration !== "all" && (
                  <FilterChip
                    label={`Duration: ${
                      durations.find((d) => d.value === filters.duration)?.label
                    }`}
                    onRemove={() => setFilter("duration", "all")}
                  />
                )}
                {filters.difficulty !== "all" && (
                  <FilterChip
                    label={`Difficulty: ${filters.difficulty}`}
                    onRemove={() => setFilter("difficulty", "all")}
                  />
                )}
                <button
                  onClick={resetFilters}
                  className="text-xs text-red-500 hover:text-red-700 font-medium px-3 py-1.5 rounded-full border border-red-200 hover:border-red-300 transition-colors"
                >
                  Clear all
                </button>
              </div>
            )}

            {/* Tour Cards Grid */}
            {filtered.length === 0 ? (
              <div className="text-center py-24">
                <div className="text-5xl mb-4">🗺️</div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                  No tours found
                </h3>
                <p className="text-gray-500 mb-6">
                  Try adjusting your filters to see more results.
                </p>
                <button onClick={resetFilters} className="btn-primary">
                  Clear Filters
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
                {filtered.map((tour) => (
                  <TourCard key={tour.id} tour={tour} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {mobileFiltersOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileFiltersOpen(false)}
          />
          <div className="absolute right-0 top-0 bottom-0 w-80 bg-white shadow-2xl overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-semibold text-gray-900 text-lg">Filters</h2>
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Close filters"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="p-5">
              <FilterPanel
                filters={filters}
                setFilter={setFilter}
                resetFilters={resetFilters}
                activeFilterCount={activeFilterCount}
                difficultyBadge={difficultyBadge}
              />
            </div>
            <div className="sticky bottom-0 p-5 border-t border-gray-100 bg-white">
              <button
                onClick={() => setMobileFiltersOpen(false)}
                className="btn-primary w-full text-center"
              >
                Show {filtered.length} Tours
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* ─── Sub-components ────────────────────────────────────────── */

interface FilterPanelProps {
  filters: Filters;
  setFilter: (key: FilterKey, value: string) => void;
  resetFilters: () => void;
  activeFilterCount: number;
  difficultyBadge: Record<string, string>;
}

function FilterPanel({
  filters,
  setFilter,
  resetFilters,
  activeFilterCount,
  difficultyBadge,
}: FilterPanelProps) {
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="font-semibold text-gray-900 text-lg">Filters</h2>
        {activeFilterCount > 0 && (
          <button
            onClick={resetFilters}
            className="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            Reset all
          </button>
        )}
      </div>

      {/* Category */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
          Category
        </h3>
        <div className="space-y-2">
          {categories.map((cat) => (
            <label
              key={cat.value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="radio"
                name="category"
                value={cat.value}
                checked={filters.category === cat.value}
                onChange={() => setFilter("category", cat.value)}
                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
              />
              <span
                className={`text-sm transition-colors ${
                  filters.category === cat.value
                    ? "text-primary-600 font-medium"
                    : "text-gray-600 group-hover:text-gray-900"
                }`}
              >
                {cat.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Duration */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
          Duration
        </h3>
        <div className="space-y-2">
          {durations.map((dur) => (
            <label
              key={dur.value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="radio"
                name="duration"
                value={dur.value}
                checked={filters.duration === dur.value}
                onChange={() => setFilter("duration", dur.value)}
                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
              />
              <span
                className={`text-sm transition-colors ${
                  filters.duration === dur.value
                    ? "text-primary-600 font-medium"
                    : "text-gray-600 group-hover:text-gray-900"
                }`}
              >
                {dur.label}
              </span>
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-100" />

      {/* Difficulty */}
      <div>
        <h3 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">
          Difficulty
        </h3>
        <div className="space-y-2">
          {difficulties.map((diff) => (
            <label
              key={diff.value}
              className="flex items-center gap-3 cursor-pointer group"
            >
              <input
                type="radio"
                name="difficulty"
                value={diff.value}
                checked={filters.difficulty === diff.value}
                onChange={() => setFilter("difficulty", diff.value)}
                className="w-4 h-4 text-primary-600 border-gray-300 focus:ring-primary-500"
              />
              <span className="flex items-center gap-2">
                <span
                  className={`text-sm transition-colors ${
                    filters.difficulty === diff.value
                      ? "text-primary-600 font-medium"
                      : "text-gray-600 group-hover:text-gray-900"
                  }`}
                >
                  {diff.label}
                </span>
                {diff.value !== "all" && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                      difficultyBadge[diff.value]
                    }`}
                  >
                    {diff.label}
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
}

function FilterChip({
  label,
  onRemove,
}: {
  label: string | undefined;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-700 text-xs font-medium px-3 py-1.5 rounded-full border border-primary-200">
      {label}
      <button
        onClick={onRemove}
        className="hover:text-primary-900 transition-colors"
        aria-label="Remove filter"
      >
        <svg
          className="w-3 h-3"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>
    </span>
  );
}

